// Where the docked mini-player's orb is, for the overlay to fly to and from.
//
// The dock portals to <body> and the overlay lives in <main>, so neither can
// hold a ref to the other — and after a hand-off only one of them is mounted.
// A rectangle in module scope is the whole contract between them.

let dockOrbRect: DOMRect | null = null
// The rect deliberately outlives its dock: the return journey flies out of
// where the dock WAS, and by then it is already gone. So the rect answers
// "where did a dock sit" and this answers "is that still a dock" — the two
// questions have different answers, and a collapse is asking the second.
let dockLive = false
let waiting: (() => void)[] = []

export function publishDockOrbRect(rect: DOMRect): void {
  dockOrbRect = rect
  dockLive = true
  if (waiting.length === 0) return
  const due = waiting
  waiting = []
  for (const notify of due) notify()
}

/** The dock is leaving. Its rect stays; it just stops standing for a dock. */
export function releaseDockOrbRect(): void {
  dockLive = false
}

/** Where the dock last sat, live or not. The return journey's starting point. */
export function readDockOrbRect(): DOMRect | null {
  return dockOrbRect
}

// Run once a dock that is actually on screen has said where it is.
//
// Only a collapse asks. The dock is behind a Portal, which renders nothing
// until its own layout effect has run, so in the commit where the overlay
// starts leaving there is no card yet — reading straight through finds either
// nothing (the first collapse of a session) or the departed dock's rect, which
// is worse because it looks like it worked until something has moved the dock
// between appearances. Measured at 244px off.
//
// A return has nothing to wait for and reads the rect directly: by then the
// dock is gone by construction.
export function whenDockOrbArrives(run: () => void): () => void {
  if (dockLive && dockOrbRect) {
    run()
    return () => {}
  }
  waiting.push(run)
  return () => {
    waiting = waiting.filter((pending) => pending !== run)
  }
}

/** Centre-to-centre move and size ratio, both in viewport coordinates. */
export function orbFlight(
  from: DOMRect,
  to: DOMRect
): { x: number; y: number; scale: number } | null {
  if (!from.width || !to.width) return null
  return {
    x: to.left + to.width / 2 - (from.left + from.width / 2),
    y: to.top + to.height / 2 - (from.top + from.height / 2),
    scale: to.width / from.width,
  }
}

// The dock's chip (40px) against the overlay orb it hands off to (96), so a
// call comes up the size it would have shrunk to on the way down. Stated
// rather than measured off a real dock: on the first call of a page load none
// has ever published, and an entrance that plays only from the second call on
// is worse than none.
const RISE_START_SCALE = 40 / 96

// A starting call rises straight up from the bottom of the window. No sideways
// travel — there is no dock over there to have come from, and a diagonal entry
// looks like a hand-off from something the user cannot see.
export function orbRise(
  orb: DOMRect,
  viewportHeight: number
): { x: number; y: number; scale: number } | null {
  if (!orb.width) return null
  return {
    x: 0,
    y: viewportHeight - (orb.top + orb.height / 2),
    scale: RISE_START_SCALE,
  }
}
